import React from "react";
import type { BraveIndex, ClaudeMemory, MentionCheck, MentionRole } from "../../../services/visibilityService";
import { engineLabel, roleTitle } from "./visibilityLabels";

/* SGL-510. Claude two ways — "finds you" (answering from Brave Search, the
 * index Claude searches) and "knows you" (no search at all) — and whether
 * Brave has the site. Every non-"ok" state says what happened; none of them
 * renders as "not mentioned". */

const NAMED: MentionRole[] = ["recommended", "mentioned"];

/** Engines that were asked for but produced nothing, with why. */
export const EngineGapsNote: React.FC<{ m: MentionCheck }> = ({ m }) => {
  const failed = m.enginesFailed ?? [];
  const unconfigured = m.enginesUnconfigured ?? [];
  if (!failed.length && !unconfigured.length) return null;
  return (
    <p className="avm-feed-note" role="note" style={{ marginTop: 8 }}>
      {failed.length > 0 && (
        <>{failed.map(engineLabel).join(", ")}: engine error this check, so {failed.length === 1 ? "it is" : "they are"} left
          out rather than counted as not mentioning you. </>
      )}
      {unconfigured.length > 0 && (
        <>{unconfigured.map(engineLabel).join(", ")}: not set up yet.</>
      )}
    </p>
  );
};

function liveClaude(m: MentionCheck): { status: "ok" | "failed" | "unconfigured" | "absent"; named: number; measured: number } {
  if ((m.enginesUnconfigured ?? []).includes("claude")) return { status: "unconfigured", named: 0, measured: 0 };
  if ((m.enginesFailed ?? []).includes("claude")) return { status: "failed", named: 0, measured: 0 };
  const col = m.matrix?.engines.indexOf("claude") ?? -1;
  if (col < 0) return { status: "absent", named: 0, measured: 0 };
  const cells = (m.matrix?.rows ?? []).map((r) => r.cells[col]).filter((c): c is MentionRole => !!c && c !== "unknown");
  return { status: "ok", named: cells.filter((c) => NAMED.includes(c)).length, measured: cells.length };
}

function memoryStat(mem: ClaudeMemory | null | undefined): { text: string; sub: string } {
  if (!mem) return { text: "—", sub: "Not measured on this check" };
  if (mem.status === "unconfigured") return { text: "—", sub: "Not set up yet" };
  if (mem.status === "failed") return { text: "—", sub: "Claude could not be reached this check" };
  return { text: `${mem.appeared_in ?? 0}/${mem.scored_rows ?? 0}`, sub: "Answers naming you, with no search" };
}

export const ClaudeCard: React.FC<{ m: MentionCheck }> = ({ m }) => {
  const live = liveClaude(m);
  const mem = m.claudeMemory;
  if (live.status === "absent" && !mem) return null;
  const memS = memoryStat(mem);
  const liveText = live.status === "ok" ? `${live.named}/${live.measured}` : "—";
  const liveSub =
    live.status === "ok" ? "Answers naming you, searching the web (Brave)"
    : live.status === "failed" ? "Engine error this check"
    : live.status === "unconfigured" ? "Not set up yet"
    : "Not measured on this check";
  const rows = mem?.status === "ok" ? mem.rows ?? [] : [];
  return (
    <section className="avm-card" aria-labelledby="avm-claude-title" style={{ marginBottom: 16 }}>
      <header>
        <h2 id="avm-claude-title">Claude: finds you vs knows you</h2>
        <span className="avm-live-tag">Live</span>
      </header>
      <p className="avm-feed-note" style={{ margin: "-6px 0 14px" }}>
        "Finds you" is Claude answering with today's search results. "Knows you" is what it says with no search,
        from what it learned in training. Fixing the first is about pages Brave can find; the second moves slowly.
      </p>
      <div className="avm-grid avm-cols-2">
        <div className="avm-kpi" style={{ boxShadow: "none" }}>
          <b className="avm-num">{liveText}</b>
          <div className="avm-lbl">Claude finds you · {liveSub}</div>
        </div>
        <div className="avm-kpi" style={{ boxShadow: "none" }}>
          <b className="avm-num">{memS.text}</b>
          <div className="avm-lbl">Claude knows you · {memS.sub}</div>
        </div>
      </div>
      {rows.length > 0 && (
        <div className="avm-matrix" style={{ marginTop: 12 }}>
          <table className="avm-mx">
            <thead>
              <tr><th className="avm-q">Asked with no search…</th><th>Claude says</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.question}>
                  <td className="avm-q">
                    “{r.question}”
                    {r.role === "absent" && r.competitors.length > 0 && (
                      <div className="avm-trend-note">Named instead: {r.competitors.slice(0, 3).join(", ")}</div>
                    )}
                  </td>
                  <td>{roleTitle(r.role)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export const BraveIndexCard: React.FC<{ index: BraveIndex | null | undefined }> = ({ index }) => {
  if (!index) return null;
  return (
    <section className="avm-card" aria-labelledby="avm-brave-index-title">
      <header>
        <h2 id="avm-brave-index-title">Brave index</h2>
        <span className="avm-meta">Brave Search · what Claude searches</span>
      </header>
      {index.status === "unconfigured" && <p className="avm-feed-note">Not set up yet.</p>}
      {index.status === "failed" && (
        <p className="avm-feed-note">Brave could not be checked this time, so we don't know yet whether it has your site.</p>
      )}
      {index.status === "ok" && (
        <>
          <div className="avm-kpi" style={{ boxShadow: "none" }}>
            <b className="avm-num">{index.indexed ? "Yes" : "No"}</b>
            <div className="avm-lbl">
              {index.indexed
                ? `${index.domain} is in Brave's index (${index.indexed_pages_seen ?? 0} page${index.indexed_pages_seen === 1 ? "" : "s"} in the first 20 results)`
                : `Brave returned no pages from ${index.domain} — Claude cannot find what Brave does not have`}
            </div>
          </div>
          {(index.question_ranks ?? []).length > 0 && (
            <>
              <h3 className="avm-eyebrow avm-subhead">
                Your rank on Brave for the check questions ({index.questions_ranked ?? 0}/{index.questions_measured ?? 0} in the top 10)
              </h3>
              <ul className="avm-trend-note" style={{ paddingLeft: 18 }}>
                {(index.question_ranks ?? []).map((r) => (
                  <li key={r.question}>
                    “{r.question}” — {r.error ? "could not check" : r.rank ? `#${r.rank}` : "not in the top 10"}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
};
